import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SwHttpClientService } from 'src/app/librarys/serves/sw-http-client.service';
import { SwJwtService } from 'src/app/librarys/serves/sw-jwt.service';

/**
 * AI流式事件。
 */
export interface AiStreamEvent {
  type: string;
  requestId?: string;
  model?: string;
  delta?: string;
  message?: string;
}

/**
 * AI流式回调参数。
 */
export interface AiStreamHandler {
  onOpen?: () => void;
  onMessage?: (event: AiStreamEvent) => void;
  onError?: (message: string) => void;
  onComplete?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class AwsService {

  private famPrefix: string = environment.famWebAPI;
  private prefix: string = environment.fileWebAPI;

  constructor(
    private http: SwHttpClientService,
    private jwt: SwJwtService
  ) { }

  tabUpdated: Subject<any> = new Subject<any>();

  getThreePreviewInfoByFileInfoId(fileInfoId: string) {
    const url = this.prefix + 'threeModel/convert';
    return this.http.Post(url, { fileInfoId: fileInfoId });
  }

  getThreePreviewFileByFileInfoId(fileInfoId: string) {
    const url = this.prefix + 'threeModel/preview/' + fileInfoId;
    return this.http.GetExport(url);
  }

  /**
   * 发起AI流式问答。
   * @param question 用户问题
   * @param handler 流式回调
   */
  streamAiChat(question: string, handler: AiStreamHandler): XMLHttpRequest {
    const url = this.famPrefix + 'aiStreamTest/ask';
    const xhr = new XMLHttpRequest();
    const token = this.jwt.getToken('token');
    const tokenSso = this.jwt.getToken('tokenSso');
    let receivedLength = 0;
    let buffer = '';
    let completed = false;

    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
    xhr.setRequestHeader('Accept', 'text/event-stream');
    if (token) {
      xhr.setRequestHeader('authorizationu', token);
    }
    if (tokenSso) {
      xhr.setRequestHeader(environment.ssoTokenName, tokenSso);
    }

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 2 && handler && handler.onOpen) {
        handler.onOpen();
      }
    };

    xhr.onprogress = () => {
      const chunk = xhr.responseText.substring(receivedLength);
      receivedLength = xhr.responseText.length;
      buffer += chunk.replace(/\r\n/g, '\n');
      buffer = this.parseStreamBuffer(buffer, handler, () => completed = true);
    };

    xhr.onerror = () => {
      if (handler && handler.onError) {
        handler.onError('流式请求失败，请检查网络或接口服务');
      }
    };

    xhr.onload = () => {
      buffer += '\n\n';
      buffer = this.parseStreamBuffer(buffer, handler, () => completed = true);
      if (xhr.status !== 200) {
        if (handler && handler.onError) {
          handler.onError('接口调用失败，状态码：' + xhr.status);
        }
        return;
      }
      if (!completed && handler && handler.onComplete) {
        handler.onComplete();
      }
    };

    xhr.send(JSON.stringify({ question: question }));
    return xhr;
  }

  private parseStreamBuffer(buffer: string, handler: AiStreamHandler, completedCallback: () => void): string {
    let normalizedBuffer = buffer;
    let splitIndex = normalizedBuffer.indexOf('\n\n');
    while (splitIndex > -1) {
      const rawEvent = normalizedBuffer.substring(0, splitIndex).trim();
      normalizedBuffer = normalizedBuffer.substring(splitIndex + 2);
      this.handleStreamEvent(rawEvent, handler, completedCallback);
      splitIndex = normalizedBuffer.indexOf('\n\n');
    }
    return normalizedBuffer;
  }

  private handleStreamEvent(rawEvent: string, handler: AiStreamHandler, completedCallback: () => void): void {
    if (!rawEvent) {
      return;
    }
    const dataLines = rawEvent
      .split('\n')
      .filter(line => line.indexOf('data:') === 0)
      .map(line => line.substring(5).trim());
    if (!dataLines.length) {
      return;
    }
    try {
      const event = JSON.parse(dataLines.join('')) as AiStreamEvent;
      if (handler && handler.onMessage) {
        handler.onMessage(event);
      }
      if (event.type === 'completed') {
        completedCallback();
        if (handler && handler.onComplete) {
          handler.onComplete();
        }
      }
      if (event.type === 'error' && handler && handler.onError) {
        handler.onError(event.message || '流式请求失败');
      }
    } catch (error) {
      const errorText = error ? JSON.stringify(error) : '';
      console.error('解析AI流式事件失败', errorText);
    }
  }
}